import {SimpleDialogData} from '../../components/simple-dialog/simple-dialog.component';
import {LOGIN_SESSION_ERROR} from '../../models/auth-response';

export type LoginFailure =
  | 'not-found'
  | 'captcha'
  | 'invalid-input'
  | 'session'
  | 'unavailable'
  | 'unexpected';

/**
 * Every value the `reason` dimension of the `loginError` analytics event can
 * take. The two extra members never produce a request, so they have no
 * `LoginFailure` counterpart, but they share the dimension so one breakdown
 * covers the whole surface.
 */
export type LoginErrorReason = LoginFailure | 'captcha-widget' | 'captcha-unavailable';

/**
 * The subset of `HttpErrorResponse` the classifier reads, plus the `name`/`cause`
 * pair carried by a tagged sign-in failure.
 */
export interface LoginErrorLike {
  name?: string;
  status?: number;
  error?: {fields?: Record<string, unknown>; message?: string} | null;
  cause?: unknown;
}

const CAPTCHA_FIELDS = ['turnstile_response', 'captcha_response'];

const isCaptchaRejection = (error: LoginErrorLike): boolean => {
  const fields = error?.error?.fields;
  if (!fields || typeof fields !== 'object') {
    return false;
  }
  // Key presence, not truthiness: the backend builds the payload from
  // `.get('error-codes')` with no default, so the value can be null when the
  // provider omits the codes. `captcha_response` is the key the backend uses
  // when `turnstile_response` arrives empty and it falls back to reCAPTCHA.
  return CAPTCHA_FIELDS.some(field => field in fields);
};

export const classifyLoginError = (error: LoginErrorLike | null | undefined): LoginFailure => {
  if (error?.name === LOGIN_SESSION_ERROR) {
    return 'session';
  }

  const status = error?.status;

  if (status === 404) {
    return 'not-found';
  }

  if (status === 400) {
    return isCaptchaRejection(error) ? 'captcha' : 'invalid-input';
  }

  if (status === 0 || status >= 500) {
    return 'unavailable';
  }

  return 'unexpected';
};

export const LOGIN_FAILURE_DIALOG: Record<LoginFailure, SimpleDialogData> = {
  'not-found': {
    okButton: 'OK',
    title: 'Usuário não encontrado',
    content: 'Confira o CPF e a data de nascimento. Se estiverem corretos, fale com o RH da sua empresa.'
  },
  captcha: {
    okButton: 'OK',
    title: 'Não foi possível validar seu acesso',
    content: 'Atualize a página e tente novamente.'
  },
  'invalid-input': {
    okButton: 'OK',
    title: 'Confira os dados informados',
    content: 'Revise o CPF e a data de nascimento.'
  },
  session: {
    okButton: 'OK',
    title: 'Não foi possível iniciar sua sessão',
    content: 'Seu navegador está bloqueando o armazenamento deste site. Libere os cookies para este endereço e tente de novo.'
  },
  unavailable: {
    okButton: 'OK',
    title: 'Não conseguimos concluir seu acesso agora',
    content: 'Tente novamente em alguns instantes. Se continuar, fale com o RH da sua empresa.'
  },
  unexpected: {
    okButton: 'OK',
    title: 'Não foi possível entrar',
    content: 'Tente novamente em alguns instantes.'
  }
};
